import { createFileRoute, Link } from "@tanstack/react-router";
import { getBusinessConfig } from "@/lib/site-config";
import { seoHead } from "@/lib/seo";
import { FAQAccordion, faqSchema } from "@/components/FAQAccordion";

const COOKIE_FAQS = [
  {
    q: "Does Woodseer Autos use tracking cookies?",
    a: "We use a small number of essential cookies to remember your consent choice and any in-progress booking form. Analytics is loaded only after you accept — and even then it's privacy-respecting and anonymised, never used for ad targeting.",
  },
  {
    q: "What happens if I decline cookies?",
    a: "The site works as normal. You'll still be able to browse, request a callback, call or WhatsApp. We just won't load analytics, so we won't know which pages helped you.",
  },
  {
    q: "How do I change my cookie choice later?",
    a: "Clear your browser's site data for woodseerautos.co.uk and the consent banner will appear again on your next visit, letting you accept or decline.",
  },
  {
    q: "Do you share cookie data with advertisers?",
    a: "No. We do not use Facebook Pixel, Google Ads, TikTok Pixel or any third-party advertising trackers. The Google Map embedded on the contact page is governed by Google's own privacy policy.",
  },
  {
    q: "Why does the WhatsApp button work without cookies?",
    a: "The WhatsApp button is a plain link. Clicking it hands you off to WhatsApp's own app or web client — no cookie is set on this site by clicking it.",
  },
  {
    q: "How long do your cookies last?",
    a: "Consent and form-state cookies expire after 12 months. Anonymous analytics cookies (when accepted) roll up after 14 months. Nothing is kept indefinitely.",
  },
];

const COOKIES_TABLE = [
  { name: "wa_consent", purpose: "Remembers whether you accepted or declined non-essential cookies.", duration: "12 months", type: "Essential" },
  { name: "wa_booking_state", purpose: "Restores your in-progress MOT/quote form if you navigate away and come back.", duration: "Session", type: "Essential" },
  { name: "_ga / _ga_*", purpose: "Anonymised page views and basic device info — only set if you accept analytics.", duration: "14 months", type: "Analytics (consent)" },
];

export const Route = createFileRoute("/cookies")({
  head: () =>
    seoHead({
      path: "/cookies",
      title: "Cookie Policy | Privacy-First Cookies | Woodseer Autos E14",
      description:
        "Plain-English cookie policy for Woodseer Autos. We use a handful of essential cookies and privacy-respecting analytics — no advertising trackers, no third-party pixels.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Cookie Policy", path: "/cookies" },
      ],
      jsonLd: [faqSchema(COOKIE_FAQS) as Record<string, unknown>],
    }),
  component: CookiePolicyPage,
});

function CookiePolicyPage() {
  const biz = getBusinessConfig();

  return (
    <>
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-4">
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">Legal</p>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold text-foreground tracking-tight">Cookie Policy</h1>
          <p className="mt-2 text-sm text-muted-foreground">Last updated: May 2026</p>

          <p className="mt-6 text-sm text-foreground/85 leading-relaxed">
            We try to use as few cookies as possible. {biz.name} does not use Facebook Pixel, Google Ads, TikTok Pixel or any third-party advertising trackers. The cookies we do use are listed below in plain English.
          </p>

          <div className="mt-10 space-y-7 text-sm text-muted-foreground leading-relaxed">
            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">1. What is a cookie?</h2>
              <p>A cookie is a small text file your browser stores when you visit a website. It can remember preferences (like consent choice) or anonymous statistics (like which pages were visited). Cookies cannot read other files on your device or identify you personally on their own.</p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">2. Cookies we use</h2>
              <div className="mt-3 overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/40 text-foreground">
                    <tr>
                      <th className="text-left p-2 font-bold">Name</th>
                      <th className="text-left p-2 font-bold">Purpose</th>
                      <th className="text-left p-2 font-bold">Duration</th>
                      <th className="text-left p-2 font-bold">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COOKIES_TABLE.map((c) => (
                      <tr key={c.name} className="border-t border-border">
                        <td className="p-2 font-mono text-foreground">{c.name}</td>
                        <td className="p-2">{c.purpose}</td>
                        <td className="p-2">{c.duration}</td>
                        <td className="p-2">{c.type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">3. Your consent choice</h2>
              <p>The first time you visit, a banner asks you to accept or decline non-essential cookies. Essential cookies (consent state, booking form) are always loaded — without them the site cannot function. Analytics cookies are only loaded after you accept.</p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">4. Changing your choice</h2>
              <p>You can change your mind at any time by clearing site data for woodseerautos.co.uk in your browser settings. The consent banner will then reappear on your next visit. Most browsers also let you block all cookies entirely under privacy settings.</p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">5. Embedded content</h2>
              <p>The Google Map on our <Link to="/contact" className="text-primary font-semibold hover:underline">contact page</Link> is provided by Google and may set its own cookies. Embedded Instagram or YouTube content (where shown) is governed by Meta's and Google's policies respectively. We don't control these and recommend reading their cookie policies if you'd like more detail.</p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">6. Contact</h2>
              <p>Questions or want us to delete a record? Email <a href={`mailto:${biz.email}`} className="text-primary font-semibold hover:underline">{biz.email}</a> with the subject "Cookie request". See also our <Link to="/privacy" className="text-primary font-semibold hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-primary font-semibold hover:underline">Terms & Conditions</Link>.</p>
            </div>
          </div>
        </div>
      </section>

      <FAQAccordion faqs={COOKIE_FAQS} title="Cookie FAQs" />
    </>
  );
}
